/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useSelector, useDispatch } from "react-redux";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";
import CircularProgress from "@material-ui/core/CircularProgress";
import { CardContent, Divider } from "@material-ui/core";

//icon
import SaveIcon from "@material-ui/icons/Save";

// jazasoft
import {
  TextInput,
  NumberInput,
  SelectArrayInput,
  SelectInput,
  BooleanInput,
  PageFooter,
  PageHeader,
  Button,
  WithReduxForm,
  BackButton,
  showNotification,
  translate,
  required
} from "jazasoft";

// local
import Forbidden from "../../../components/Forbidden";
import CardHeader from "../../../components/CardHeader";
import MuiTable from "../../../components/MuiTable";

// helper
import { ProductFields } from "../../../utils/entityFields";
import SettingKeys from "../../../utils/SettingKeys";
import getValidation from "../../../utils/getValidation";
import { inputOptions, isTrue, isEmpty, listFromCsv } from "../../../utils/helpers";

import useMutation from "../../../hooks/useMutation";
import useSetting from "../../../hooks/useSetting";
import useQuery from "../../../hooks/useQuery";
import useUnit from "../../../hooks/useUnit";
import useDeepMemo from "../../../hooks/useDeepMemo";
import { Role } from "../../../utils/types";

const parameters = ["Lot", "Style", "Color", "BundleOrder", "Lay", "Size", "Lay-Size-Ratio", "SizeGroup", "Bundle", "Roll", "Shade", "Ratio"];

const choicesSerialReset = parameters.filter(e => !["BundleOrder", "Shade", "Roll", "Ratio"].includes(e)).map(e => ({ id: e, name: e }));
const choicesBundleReset = parameters.filter(e => !["Lay-Size-Ratio", "Bundle", "Shade", "Roll", "Ratio"].includes(e)).map(e => ({ id: e, name: e }));
const choicesBundleBreak = parameters.filter(e => ["Roll", "Shade", "Ratio"].includes(e)).map(e => ({ id: e, name: e }));
const choicesSizeSort = [
  { id: "asc", name: "Ascending" },
  { id: "desc", name: "Descending" }
];
const choicesSerialSort = [
  { id: "Top to Bottom", name: "Top to Bottom" },
  { id: "Bottom to Top", name: "Bottom to Top" }
];
const useStyles = makeStyles(theme => ({
  root: {
    padding: "1.5em"
  },
  marginVertical: {
    marginTop: "1em",
    marginBottom: "2em"
  },
  pageFooter: {
    "& > button": {
      marginRight: theme.spacing(2)
    }
  }
}));

const ProductEdit = props => {
  const dispatch = useDispatch();
  const [mutate, { loading }] = useMutation();
  const classes = useStyles();
  const { id, i18nKey, translate, resource, basePath, roles, history } = props;

  const fields = useSetting(SettingKeys.ENTITY_FIELDS, resource);
  const validationRule = useSetting(SettingKeys.ENTITY_FIELD_VALIDATION, resource);

  // api call
  const { data: productList = [] } = useQuery({
    url: "products",
    params: { size: 1000, search: `(isSet=='NULL',isSet=='FALSE')` }
  });

  const { data: product = {} } = useQuery({ url: `products/${id}` });

  const formValues = useSelector(state => state.form["record-form"] && state.form["record-form"].values);
  const isSet = formValues?.isSet;

  const { unitChoices: unitList } = useUnit(product);

  const onSubmit = values => {
    let options = {
      url: `${resource}/${product?.id}`,
      method: "put",
      data: {
        ...values,
        productGroupIds: !isEmpty(values?.productGroupId) ? [values?.productGroupId] : values?.productGroupIds,
        unitIds: values?.unitIds?.join(","),
        serialResetAt: values?.serialResetAt?.join(","),
        bundleResetAt: values?.bundleResetAt?.join(","),
        bundleBreakAt: values?.bundleBreakAt?.join(",")
      }
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("js.notification.created", { resource: i18nKey }));
        props.history.push(basePath);
      }
    });
  };

  const onSetSubmit = (productList, form, onActionComplete) => {
    productList = productList.filter(e => !isEmpty(e.productId));
    if (isEmpty(productList)) {
      dispatch(showNotification("At least one product is required in Product Set", { type: "danger" }));
      return;
    }

    let options = {
      url: `${resource}/${product?.id}`,
      method: "put",
      data: {
        ...formValues,
        unitIds: formValues?.unitIds?.join(","),
        productList,
        serialResetAt: formValues?.serialResetAt?.join(","),
        bundleResetAt: formValues?.bundleResetAt?.join(","),
        bundleBreakAt: formValues?.bundleBreakAt?.join(",")
      }
    };

    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("js.notification.created", { resource: i18nKey }));
        onActionComplete && onActionComplete();
        props.history.push(basePath);
      }
    });
  };

  const columns = useDeepMemo(() => {
    return [
      { dataKey: "productId", title: "Product", inputType: "select-input", choices: productList },
      { dataKey: "ratio", title: "Ratio", inputType: "text-input", options: { type: "number" } }
    ];
  }, [productList]);

  const rows = useDeepMemo(() => {
    if (isEmpty(product?.productList) || !isSet) return [];
    return product.productList.map(e => ({ ...e, productId: e.product?.id }));
  }, [product, isSet]);

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  if (!(roles?.includes(Role.MASTER) || roles?.includes(Role.SUPER_USER))) {
    return <Forbidden history={history} />;
  }
  return (
    <div className={classes.root}>
      <PageHeader title="resources.products.pageHeader.create" titleArgs={{ _: "Edit Product" }} />

      <WithReduxForm
        initialValues={{
          ...product,
          serialResetAt: listFromCsv(product.serialResetAt),
          bundleResetAt: listFromCsv(product.bundleResetAt),
          bundleBreakAt: listFromCsv(product.bundleBreakAt),
          unitIds: listFromCsv(product?.unitIds)
        }}>
        {({ handleSubmit }) => (
          <div>
            <Card className={classes.marginVertical}>
              <CardContent>
                <Grid container spacing={2}>
                  {unitList?.length > 1 && (
                    <Grid item xs={12} sm={3}>
                      <SelectArrayInput
                        i18nKey={i18nKey}
                        validate={[required()]}
                        source="unitIds"
                        label="Units"
                        choices={unitList}
                        {...inputOptions(3)}
                      />
                    </Grid>
                  )}
                  {fields.includes(ProductFields.NAME) && (
                    <Grid item xs={12} sm={3}>
                      <TextInput i18nKey={i18nKey} source="name" validate={getValidation(validationRule[ProductFields.NAME])} {...inputOptions(3)} />
                    </Grid>
                  )}

                  {fields.includes(ProductFields.BUNDLE_PARTS) && (
                    <Grid item xs={12} sm={3}>
                      <TextInput
                        i18nKey={i18nKey}
                        source="bundleParts"
                        validate={getValidation(validationRule[ProductFields.BUNDLE_PARTS])}
                        {...inputOptions(3)}
                      />
                    </Grid>
                  )}

                  {fields.includes(ProductFields.REPLACEMENT_PANELS) && (
                    <Grid item xs={12} sm={3}>
                      <TextInput
                        i18nKey={i18nKey}
                        source="replacementPanels"
                        validate={getValidation(validationRule[ProductFields.REPLACEMENT_PANELS])}
                        {...inputOptions(3)}
                      />
                    </Grid>
                  )}

                  {fields.includes(ProductFields.SERIAL_RESET_AT) && (
                    <Grid item xs={12} sm={3}>
                      <SelectArrayInput
                        i18nKey={i18nKey}
                        source="serialResetAt"
                        label="Serial Reset At"
                        choices={choicesSerialReset}
                        {...inputOptions(3)}
                      />
                    </Grid>
                  )}

                  {fields.includes(ProductFields.BUNDLE_RESET_AT) && (
                    <Grid item xs={12} sm={3}>
                      <SelectArrayInput
                        i18nKey={i18nKey}
                        source="bundleResetAt"
                        label="Bundle Reset At"
                        choices={choicesBundleReset}
                        {...inputOptions(3)}
                      />
                    </Grid>
                  )}

                  {fields.includes(ProductFields.BUNDLE_BREAK_AT) && (
                    <Grid item xs={12} sm={3}>
                      <SelectArrayInput
                        i18nKey={i18nKey}
                        source="bundleBreakAt"
                        label="Bundle Break At"
                        choices={choicesBundleBreak}
                        {...inputOptions(3)}
                      />
                    </Grid>
                  )}

                  {fields.includes(ProductFields.BUNDLE_SIZE_SORT) && (
                    <Grid item xs={12} sm={3}>
                      <SelectInput
                        i18nKey={i18nKey}
                        source="bundleSizeSort"
                        label="Bundling - Size Sorting"
                        choices={choicesSizeSort}
                        {...inputOptions(3)}
                      />
                    </Grid>
                  )}

                  {fields.includes(ProductFields.BUNDLE_SERIAL_SORT) && (
                    <Grid item xs={12} sm={3}>
                      <SelectInput
                        i18nKey={i18nKey}
                        source="bundleSerialSort"
                        label="Numbering - Serial Sorting"
                        choices={choicesSerialSort}
                        {...inputOptions(3)}
                      />
                    </Grid>
                  )}

                  {fields.includes(ProductFields.max_BOM_CU) && (
                    <Grid item xs={12} sm={3}>
                      <NumberInput
                        i18nKey={i18nKey}
                        source="maxBomCu"
                        validate={getValidation(validationRule[ProductFields.max_BOM_CU])}
                        {...inputOptions(3)}
                      />
                    </Grid>
                  )}

                  {fields.includes(ProductFields.DESC) && (
                    <Grid item xs={12} sm={3}>
                      <TextInput i18nKey={i18nKey} source="desc" validate={getValidation(validationRule[ProductFields.DESC])} {...inputOptions(3)} />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={3}>
                    <BooleanInput i18nKey={i18nKey} source="isSet" style={{ paddingLeft: "2em", marginTop: "1.5em" }} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {isSet && (
              <Card>
                <CardHeader title="Products in Set" />
                <Divider />
                <div>
                  <MuiTable
                    columns={columns}
                    rows={rows}
                    component="div"
                    editable
                    editing
                    showEditableActions
                    rowAddCount={1}
                    handleSubmitRef={handleSubmitRef}
                    onSubmit={onSetSubmit}
                  />
                </div>
              </Card>
            )}

            <PageFooter className={classes.pageFooter}>
              <Button
                label={translate(`resources.products.button.save`, { _: "Save" })}
                color="primary"
                variant="contained"
                disabled={false}
                onClick={event => {
                  if (isTrue(isSet)) {
                    handleSubmit(() => {})();
                    ffHandleSubmit(event);
                  } else {
                    handleSubmit(onSubmit)();
                  }
                }}>
                <SaveIcon />
                {loading && <CircularProgress size={20} thickness={2} />}
              </Button>
              <BackButton variant="contained" />
            </PageFooter>
          </div>
        )}
      </WithReduxForm>
    </div>
  );
};

export default translate(ProductEdit);
